import {
  ActionIcon,
  Badge,
  Button,
  Modal,
  Pagination,
  Select,
  Table,
  TextInput,
} from "@mantine/core";
import React, { useEffect, useState } from "react";
import AddUserModal from "./AddUserModal";
import { getAllUsers, getBuData } from "../apis/userApis";
import { IconEdit, IconSearch, IconSortAscending } from "@tabler/icons-react";
import { mapRoleToLabel } from "./utilities";

const sortOptions = [
  { label: "Created", value: "createdAt" },
  { label: "Name", value: "name" },
  { label: "Email", value: "email" },
];

const UserList = () => {
  const [userModal, setUserModal] = useState(false);
  const [dataToUpdate, setDataToUpdate] = useState(null);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchString, setSearchString] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [buMap, setBuMap] = useState({});

  // No debounce library in this app's dependencies; a small manual timer
  // avoids firing a search request on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setSearchString(searchInput), 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const getBuLabelData = async () => {
      const response = await getBuData();
      if (response.success) {
        setBuMap(
          response.formattedResponse.reduce((acc, bu) => {
            acc[bu.value] = bu.label;
            return acc;
          }, {})
        );
      }
    };
    getBuLabelData();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      const response = await getAllUsers({ page, searchString, sortBy, sortDirection });
      if (response.success) {
        setList(response.users);
        setCount(response.count);
      }
      setLoading(false);
    };
    fetchUsers();
  }, [page, searchString, sortBy, sortDirection, reload]);

  const reloadagain = () => setReload(!reload);

  const businessUnitLabels = (businessUnits) =>
    (businessUnits || "")
      .split(",")
      .filter(Boolean)
      .map((id) => buMap[id] || id)
      .join(", ") || "-";

  return (
    <div className="m-4">
      <Modal
        size="calc(80vw)"
        opened={userModal}
        onClose={() => {
          setUserModal(false);
          setDataToUpdate(null);
        }}
        title={dataToUpdate ? "update user details" : "add user"}
      >
        <AddUserModal
          reload={reloadagain}
          dataToUpdate={dataToUpdate}
          setUserModal={setUserModal}
        />
      </Modal>

      <div className="flex justify-between mb-4">
        <Button
          style={{ backgroundColor: "rgb(117, 226, 255)" }}
          variant="filled"
          onClick={() => {
            setDataToUpdate(null);
            setUserModal(true);
          }}
        >
          Add User
        </Button>
      </div>

      <div className="flex w-full justify-between items-center mb-4">
        <TextInput
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            setPage(1);
          }}
          placeholder="Search by name, email, or phone..."
          icon={<IconSearch size={14} />}
        />
        <div className="flex items-center">
          Sort:
          <Select
            className="mx-2"
            value={sortBy}
            onChange={(val) => setSortBy(val)}
            data={sortOptions}
          />
          <ActionIcon
            onClick={() =>
              setSortDirection(sortDirection === "asc" ? "desc" : "asc")
            }
            color="#1b6f8d"
            className={sortDirection === "asc" ? "" : "rotate-180"}
          >
            <IconSortAscending />
          </ActionIcon>
        </div>
      </div>

      {!loading && list !== null && (
        <>
          <Table striped>
            <thead>
              <tr>
                <th style={{ textAlign: "center" }}>id</th>
                <th style={{ textAlign: "center" }}>name</th>
                <th style={{ textAlign: "center" }}>email</th>
                <th style={{ textAlign: "center" }}>role</th>
                <th style={{ textAlign: "center" }}>business unit/s</th>
                <th style={{ textAlign: "center" }}>phone</th>
                <th style={{ textAlign: "center" }}>enabled</th>
                <th>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {list.map((row) => (
                <tr key={row.id}>
                  <td className="text-center">{row.id}</td>
                  <td className="text-center">{row.name}</td>
                  <td className="text-center">{row.email}</td>
                  <td className="text-center">
                    {(row.userRoles || "")
                      .split(",")
                      .filter(Boolean)
                      .map((role) => mapRoleToLabel(role))
                      .join(", ") || "-"}
                  </td>
                  <td className="text-center">{businessUnitLabels(row.businessUnits)}</td>
                  <td className="text-center">{row.phone || "NA"}</td>
                  <td className="text-center">
                    <Badge color={row.enabled ? "indigo" : "red"}>
                      {row.enabled ? "yes" : "no"}
                    </Badge>
                  </td>
                  <td className="text-center">
                    <ActionIcon
                      color="white"
                      onClick={() => {
                        setDataToUpdate(row);
                        setUserModal(true);
                      }}
                    >
                      <IconEdit size={24} />
                    </ActionIcon>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {count === 0 && (
            <div className="flex justify-center items-center mt-4">
              <div>No users found.</div>
            </div>
          )}
          {count > 0 && (
            <div className="flex flex-row justify-center my-4">
              <Pagination
                page={page}
                onChange={(newPage) => setPage(newPage)}
                total={Math.ceil(count / 10)}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserList;
